import React, { useState, useEffect, useReducer, useContext } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import Paper from '@material-ui/core/Paper';

import TicketListItem from '../TicketListItemCustom';
import TicketsListSkeleton from '../TicketsListSkeleton';

import useTickets from '../../hooks/useTickets';
import { i18n } from '../../translate/i18n';
import { AuthContext } from '../../context/Auth/AuthContext';
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  ticketsListWrapper: {
    position: 'relative',
    display: 'flex',
    height: '100%',
    flexDirection: 'column',
    overflow: 'hidden',
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },

  ticketsList: {
    flex: 1,
    maxHeight: '100%',
    overflowY: 'scroll',
    ...theme.scrollbarStyles,
    borderTop: '2px solid rgba(0, 0, 0, 0.12)',
  },

  ticketsListHeader: {
    color: 'rgb(67, 83, 105)',
    zIndex: 2,
    backgroundColor: 'white',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },

  ticketsCount: {
    fontWeight: 'normal',
    color: 'rgb(104, 121, 146)',
    marginLeft: '8px',
    fontSize: '14px',
  },

  noTicketsText: {
    textAlign: 'center',
    color: 'rgb(104, 121, 146)',
    fontSize: '14px',
    lineHeight: '1.4',
  },

  noTicketsTitle: {
    textAlign: 'center',
    fontSize: '16px',
    fontWeight: '600',
    margin: '0px',
  },

  noTicketsDiv: {
    display: 'flex',
    height: '100px',
    margin: 40,
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const reducer = (state, action) => {
  if (action.type === 'LOAD_TICKETS') {
    const newTickets = action.payload;

    newTickets.forEach((ticket) => {
      const ticketIndex = state.findIndex((t) => t.id === ticket.id);
      if (ticketIndex !== -1) {
        state[ticketIndex] = ticket;
        if (ticket.unreadMessages > 0) {
          state.unshift(state.splice(ticketIndex, 1)[0]);
        }
      } else {
        state.push(ticket);
      }
    });

    return [...state];
  }

  if (action.type === 'RESET_UNREAD') {
    const ticketId = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      state[ticketIndex].unreadMessages = 0;
    }

    return [...state];
  }

  if (action.type === 'UPDATE_TICKET') {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
    } else {
      state.unshift(ticket);
    }

    return [...state];
  }

  if (action.type === 'UPDATE_TICKET_UNREAD_MESSAGES') {
    const ticket = action.payload;

    const ticketIndex = state.findIndex((t) => t.id === ticket.id);
    if (ticketIndex !== -1) {
      state[ticketIndex] = ticket;
      state.unshift(state.splice(ticketIndex, 1)[0]);
    } else {
      state.unshift(ticket);
    }

    return [...state];
  }

  if (action.type === 'UPDATE_TICKET_CONTACT') {
    const contact = action.payload;
    const ticketIndex = state.findIndex((t) => t.contactId === contact.id);
    if (ticketIndex !== -1) {
      state[ticketIndex].contact = contact;
    }
    return [...state];
  }

  if (action.type === 'DELETE_TICKET') {
    const ticketId = action.payload;
    if (!ticketId) return state;
    return state.filter((t) => Number(t.id) !== Number(ticketId));
  }

  if (action.type === 'RESET') {
    return [];
  }
};

const TicketsListGroup = (props) => {
  const {
    status,
    searchParam,
    tags,
    users,
    showAll,
    selectedQueueIds,
    selectedQueueOptionIds,
    updateCount,
    style,
  } = props;
  const classes = useStyles();
  const [pageNumber, setPageNumber] = useState(1);
  let [ticketsList, dispatch] = useReducer(reducer, []);
  const socketManager = useContext(SocketContext);
  const { user } = useContext(AuthContext);
  const { profile, queues } = user;

  useEffect(() => {
    dispatch({ type: 'RESET' });
    setPageNumber(1);
  }, [status, searchParam, dispatch, showAll, tags, users, selectedQueueIds, selectedQueueOptionIds]);

  const { tickets, hasMore, loading } = useTickets({
    pageNumber,
    searchParam,
    status,
    showAll,
    tags: JSON.stringify(tags),
    users: JSON.stringify(users),
    queueIds: JSON.stringify(selectedQueueIds),
    queueOptionIds: JSON.stringify(selectedQueueOptionIds),
  });

  useEffect(() => {
    const queueIds = queues.map(q => q.id);
    const queueOptionIds = user?.queueOptions?.map(q => q.id) || [];
    const filteredTickets = tickets.filter(
      t => queueIds.indexOf(t.queueId) > -1 || (t.queueOptionId && queueOptionIds.indexOf(t.queueOptionId) > -1) || t.userId === user.id
    );

    if (profile === "user" && user?.allTicket !== "enabled") {
      dispatch({ type: "LOAD_TICKETS", payload: filteredTickets });
    } else {
      dispatch({ type: "LOAD_TICKETS", payload: tickets });
    }
  }, [tickets, status, searchParam, queues, profile, user]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    const shouldUpdateTicket = ticket => {
      if (showAll || user?.allTicket === "enabled" || user?.profile === "admin") {
        return (
          ticket.queueId === null ||
          selectedQueueIds.indexOf(ticket.queueId) > -1 ||
          (selectedQueueOptionIds && selectedQueueOptionIds.length > 0 && ticket.queueOptionId && selectedQueueOptionIds.indexOf(ticket.queueOptionId) > -1) ||
          ticket.userId === user?.id
        );
      } else {
        return (
          ticket.userId === user?.id ||
          selectedQueueIds.indexOf(ticket.queueId) > -1 ||
          (selectedQueueOptionIds && selectedQueueOptionIds.length > 0 && ticket.queueOptionId && selectedQueueOptionIds.indexOf(ticket.queueOptionId) > -1) ||
          (selectedQueueIds.length === 0 && ticket.queueId === null)
        );
      }
    };
    const onReady = () => {
      if (status) {
        socket.emit("joinTickets", status);
      } else {
        socket.emit("joinNotification");
      }
    };

    const onTicketEvent = (data) => {
      
      if (data.action === "updateUnread") {
        dispatch({
          type: "RESET_UNREAD",
          payload: data.ticketId,
        });
      }

      if (data.action === "update" && shouldUpdateTicket(data.ticket) && data.ticket.status === status) {
        dispatch({
          type: "UPDATE_TICKET",
          payload: data.ticket,
        });
      }

      if (
        data.action === "update" &&
        data.ticket.status !== status &&
        status !== undefined
      ) {
        dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
      }

      if (data.action === "update" && !shouldUpdateTicket(data.ticket)) {
        dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
      }
    };

    const onAppMessageEvent = (data) => {
      // Ignorar eventos de fora do expediente (sem ticket para atualizar)
      if (data.action === "outOfHours") return;
      if (!data.ticket) return;

      const queueIds = queues.map((q) => q.id);
      const userQueueOptionIds = user?.queueOptions?.map((q) => q.id) || [];
      const isInUserQueue = queueIds.indexOf(data.ticket.queueId) > -1;
      const isInUserQueueOption = data.ticket.queueOptionId && userQueueOptionIds.indexOf(data.ticket.queueOptionId) > -1;
      const isAssignedToMe = data.ticket.userId === user?.id;
      const isNotUserQueue = !isInUserQueue && !isInUserQueueOption && !isAssignedToMe;
      
      if (
        profile === "user" && user?.allTicket !== "enabled" && isNotUserQueue
      ) {
        return;
      }

      if (data.action === "create" && shouldUpdateTicket(data.ticket) && ( status === undefined || data.ticket.status === status)) {
        dispatch({
          type: "UPDATE_TICKET_UNREAD_MESSAGES",
          payload: data.ticket,
        });
      }

      // ── Atualizar ticket na lista quando chega nova mensagem (action update) ──
      if (
        data.action === "update" &&
        data.ticket &&
        shouldUpdateTicket(data.ticket) &&
        (status === undefined || data.ticket.status === status)
      ) {
        dispatch({
          type: "UPDATE_TICKET",
          payload: data.ticket,
        });
      }
    };

    const onPresenceEvent = (data) => {
      dispatch({
        type: "UPDATE_TICKET_PRESENCE",
        payload: data,
      });
    };

    const onContactEvent = (data) => {
      if (data.action === "update") {
        dispatch({
          type: "UPDATE_TICKET_CONTACT",
          payload: data.contact,
        });
      }
    };

    socket.on("ready", onReady);
    socket.on(`company-${companyId}-ticket`, onTicketEvent);
    socket.on(`company-${companyId}-appMessage`, onAppMessageEvent);
    socket.on(`company-${companyId}-presence`, onPresenceEvent);
    socket.on(`company-${companyId}-contact`, onContactEvent);

    return () => {
      socket.off("ready", onReady);
      socket.off(`company-${companyId}-ticket`, onTicketEvent);
      socket.off(`company-${companyId}-appMessage`, onAppMessageEvent);
      socket.off(`company-${companyId}-presence`, onPresenceEvent);
      socket.off(`company-${companyId}-contact`, onContactEvent);
    };
  }, [status, showAll, user, selectedQueueIds, selectedQueueOptionIds, tags, users, profile, queues, socketManager]);

  useEffect(() => {
    const count = ticketsList.filter((ticket) => ticket.isGroup).length;
    if (typeof updateCount === 'function') {
      updateCount(count);
    }
  }, [ticketsList, updateCount]);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;

    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  if (status) {
    ticketsList = ticketsList.filter(ticket => ticket?.status === status)

  }

  if (selectedQueueIds.length > 0 || (selectedQueueOptionIds && selectedQueueOptionIds.length > 0)) {
    ticketsList = !!status ? ticketsList.filter(ticket => {
      if (user.profile === 'user') {
        return selectedQueueIds.includes(ticket?.queueId) || (selectedQueueOptionIds && selectedQueueOptionIds.includes(ticket?.queueOptionId));
      } else {
        return selectedQueueIds.includes(ticket?.queueId) || ticket?.queueId === null || (selectedQueueOptionIds && selectedQueueOptionIds.includes(ticket?.queueOptionId));
      }
    }) : ticketsList;
  }

  return (
    <Paper className={classes.ticketsListWrapper} style={style}>
      <Paper
        square
        name='closed'
        elevation={0}
        className={classes.ticketsList}
        onScroll={handleScroll}
      >
        <List style={{ paddingTop: 0 }}>
          {ticketsList.length === 0 && !loading ? (
            <div className={classes.noTicketsDiv}>
              <span className={classes.noTicketsTitle}>
                {i18n.t('ticketsList.noTicketsTitle')}
              </span>
              <p className={classes.noTicketsText}>
                {i18n.t('ticketsList.noTicketsMessage')}
              </p>
            </div>
          ) : (
            <>
              {ticketsList
                .filter((ticket) => ticket.isGroup === true || ticket.isGroup === "true" || ticket.isGroup === 1)
                .map((ticket) => (
                  <TicketListItem ticket={ticket} key={ticket.id} />
                ))}
            </>
          )}
          {loading && <TicketsListSkeleton />}
        </List>
      </Paper>
    </Paper>
  );
};

export default TicketsListGroup;
